class GGNav extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll("gg-nav-item"));
    this.itemByFragment = new Map();
    this.items.forEach((item) => {
      const id = item.getAttribute("href").replace("#", "");
      const sec = document.getElementById(id);
      if (sec) this.itemByFragment.set(sec, item);
    });

    if (this.itemByFragment.size) this.observeFragments();
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  observeFragments() {
    this.visible = new Set();
    this.onIntersect = this.onIntersect.bind(this);
    this.io = new IntersectionObserver(this.onIntersect, {
      rootMargin: `-${this.offsetHeight + 16}px 0px -50% 0px`,
      threshold: 0,
    });
    this.itemByFragment.forEach((_, sec) => this.io.observe(sec));
    this.pickActive();
  }

  onIntersect(entries) {
    entries.forEach((e) => {
      if (e.isIntersecting) this.visible.add(e.target);
      else this.visible.delete(e.target);
    });
    this.pickActive();
  }

  pickActive() {
    const bestSec = this.findTopVisible() ?? this.findClosestAboveFold();
    const activeItem = bestSec ? this.itemByFragment.get(bestSec) : null;
    this.items.forEach((item) => {
      const becameActive = item.setActive(item === activeItem);
      if (becameActive) this.scrollItemIntoView(item);
    });
  }

  scrollItemIntoView(item) {
    const left = item.offsetLeft - (this.clientWidth - item.offsetWidth) / 2;
    this.scrollTo({ left, behavior: "smooth" });
  }

  findTopVisible() {
    let bestSec = null;
    let bestTop = Infinity;
    this.visible.forEach((sec) => {
      const top = sec.getBoundingClientRect().top;
      if (top < bestTop) {
        bestTop = top;
        bestSec = sec;
      }
    });
    return bestSec;
  }

  findClosestAboveFold() {
    const navH = this.offsetHeight;
    let bestSec = null;
    let closestTop = -Infinity;
    this.itemByFragment.forEach((_, sec) => {
      const top = sec.getBoundingClientRect().top;
      if (top <= navH + 16 && top > closestTop) {
        closestTop = top;
        bestSec = sec;
      }
    });
    return bestSec;
  }
}

customElements.define("gg-nav", GGNav);
class GGNavItem extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
  }

  get fragmentId() {
    return this.getAttribute("href")?.replace("#", "") ?? "";
  }

  get fragment() {
    const id = this.fragmentId;
    return id ? document.getElementById(id) : null;
  }

  setActive(isActive) {
    const wasActive = this.classList.contains("active");
    this.classList.toggle("active", isActive);
    return isActive && !wasActive;
  }

  onClick(e) {
    const target = this.fragment;
    if (!target) return;
    e.preventDefault();
    const nav = this.closest("gg-nav");
    const navH = nav?.offsetHeight ?? 0;
    const top = target.getBoundingClientRect().top + window.scrollY - navH;
    window.scrollTo({ top, behavior: "smooth" });
  }
}

customElements.define("gg-nav-item", GGNavItem);

class GGTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = this.querySelectorAll("gg-tab");
    this.cards = this.querySelectorAll("gg-tab-card");
    this.contents = this.querySelectorAll("gg-tab-content");
    this.eventLabel = this.dataset.ga4EventLabel || null;

    this.applyContent = this.applyContent.bind(this);
    this.applyFilter = this.applyFilter.bind(this);
    this.onTabClick = this.onTabClick.bind(this);

    this.applyFilter(this.dataset.initialFilter);
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", this.onTabClick);
    });
  }

  onTabClick(e) {
    const tab = e.currentTarget;
    this.tabs.forEach((t) => {
      t.classList.remove("is-active");
      t.setAttribute("aria-selected", "false");
    });
    tab.classList.add("is-active");
    tab.setAttribute("aria-selected", "true");
    this.applyFilter(tab.dataset.filter);
    this.applyContent(tab.dataset.catId);
  }
  applyFilter(filter = "") {
    const formattedFilter = filter.trim();
    this.cards.forEach(function (card) {
      const filters = (card.dataset.filters || "").split(/\s+/);
      const show =
        formattedFilter === "" || filters.indexOf(formattedFilter) !== -1;
      card.classList.toggle("is-hidden", !show);
    });
  }

  applyContent(catId) {
    this.contents.forEach(function (panel) {
      panel.classList.toggle(
        "is-active",
        panel.dataset.catContent === catId,
      );
    });
  }

  disconnectedCallback() {
    this.tabs.forEach((tab) => {
      tab.removeEventListener("click", this.onTabClick);
    });
  }
}

customElements.define("gg-tabs", GGTabs);

class GGTab extends HTMLElement {}

customElements.define("gg-tab", GGTab);

class GGTabContent extends HTMLElement {
  connectedCallback() {}
}

customElements.define("gg-tab-content", GGTabContent);

class GGTabCard extends HTMLElement {
  connectedCallback() {}
}

customElements.define("gg-tab-card", GGTabCard);

class GGToggleActive extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.eventNameSelector = this.dataset.eventNameSelector || "";
    this.ga4EventName = this.dataset.ga4EventName || null;
    this.addEventListener("click", this.onClick);
  }
  onClick() {
    const open = this.classList.toggle("is-open");
    this.setAttribute("aria-expanded", open ? "true" : "false");
    if (
      open &&
      typeof fireCustomGA4Event === "function" &&
      this.eventNameSelector &&
      this.ga4EventName
    ) {
      const namesEl = this.querySelector(this.eventNameSelector);
      fireCustomGA4Event(this.ga4EventName, {
        combination: namesEl ? (namesEl.textContent || "").trim() : "",
      });
    }
  }
}

this.customElements.define("gg-toggle-active", GGToggleActive);

class GGShowMore extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
  }

  onClick() {
    const target = this.dataset.toggleTarget
      ? document.querySelector(this.dataset.toggleTarget)
      : null;
    if (!target) return;

    const expanded = target.classList.toggle("is-expanded");
    this.setAttribute("aria-expanded", expanded ? "true" : "false");
    const label = expanded ? this.dataset.lessLabel : this.dataset.moreLabel;
    if (label != null) this.textContent = label;

    if (this.dataset.scrollTarget) {
      const anchor = document.getElementById(this.dataset.scrollTarget);
      if (anchor) {
        const nav = document.querySelector("gg-nav");
        const navH = nav ? nav.offsetHeight : 0;
        const top = anchor.getBoundingClientRect().top + window.scrollY - navH;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }

    if (typeof fireCustomGA4Event === "function" && this.dataset.ga4EventName) {
      fireCustomGA4Event(this.dataset.ga4EventName, { expanded });
    }
  }
}

customElements.define("gg-show-more", GGShowMore);

class GGQuiz extends HTMLElement {
  connectedCallback() {
    this.format = this.dataset.stepLabelFormat || "{current} / {total}";
    this.completedLabel = this.dataset.completedLabel || "";
    this.fill = this.querySelector("[data-gg-quiz-fill]");
    this.stepLabel = this.querySelector("[data-gg-quiz-step-label]");
    this.resultEl = this.querySelector("gg-quiz-result");
    this.noMatchEl = this.querySelector("[data-gg-quiz-no-match]");

    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);

    if (this.steps.length) this.show(0);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
  }

  get steps() {
    return this.querySelectorAll("gg-quiz-step");
  }

  get variants() {
    return this.querySelectorAll("gg-quiz-variant");
  }

  selectedTags() {
    const tags = [];
    this.steps.forEach((step) => {
      const sel = step.querySelector("gg-quiz-option.is-selected");
      if (sel) {
        const t = sel.dataset.ggQuizTag;
        if (t) tags.push(t);
      }
    });
    return tags;
  }

  updateProgress(index, total) {
    if (this.fill) this.fill.style.width = ((index + 1) / total) * 100 + "%";
    if (this.stepLabel) {
      this.stepLabel.textContent = this.format
        .replace("{current}", index + 1)
        .replace("{total}", total);
    }
  }

  show(index) {
    const all = this.steps;
    const total = all.length;
    if (!total) return;
    all.forEach((el, i) => {
      el.hidden = i !== index;
    });
    if (this.resultEl) this.resultEl.hidden = true;
    this.updateProgress(index, total);
  }

  pickVariant(tags) {
    let best = null;
    let bestSize = -1;
    this.variants.forEach((v) => {
      const required = (v.dataset.tags || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!required.length) return;
      for (const r of required) {
        if (!tags.includes(r)) return;
      }
      if (required.length > bestSize) {
        best = v;
        bestSize = required.length;
      }
    });
    return best;
  }

  showResult() {
    this.steps.forEach((el) => {
      el.hidden = true;
    });
    if (this.resultEl) this.resultEl.hidden = false;
    if (this.fill) this.fill.style.width = "100%";
    if (this.stepLabel && this.completedLabel)
      this.stepLabel.textContent = this.completedLabel;

    const tags = this.selectedTags();
    this.dataset.ggQuizDebugTags = tags.join(" ");
    const match = this.pickVariant(tags);
    this.variants.forEach((v) => {
      v.hidden = v !== match;
    });
    if (this.noMatchEl) this.noMatchEl.hidden = !!match;
    if (typeof fireCustomGA4Event === "function") {
      fireCustomGA4Event("gg_submit_quiz", {
        selected_tags: tags.join(","),
        has_match: !!match,
      });
    }
  }

  reset() {
    this.querySelectorAll("gg-quiz-option.is-selected").forEach((b) => {
      b.classList.remove("is-selected");
    });
    this.show(0);
    this.scrollIntoViewOffset();
    if (typeof fireCustomGA4Event === "function") {
      fireCustomGA4Event("gg_click_quiz_reset");
    }
  }

  scrollIntoViewOffset() {
    const nav = document.querySelector("gg-nav");
    const navH = nav ? nav.offsetHeight : 0;
    const top = this.getBoundingClientRect().top + window.scrollY - navH;
    window.scrollTo({ top, behavior: "smooth" });
  }

  currentStepIndex() {
    let current = -1;
    this.steps.forEach((el, i) => {
      if (!el.hidden) current = i;
    });
    return current;
  }

  onClick(e) {
    const opt = e.target.closest("gg-quiz-option");
    if (opt) {
      const group = opt.closest(".gg-quiz__options");
      if (group) {
        group.querySelectorAll("gg-quiz-option").forEach((b) => {
          b.classList.remove("is-selected");
        });
      }
      opt.classList.add("is-selected");
      if (typeof fireCustomGA4Event === "function") {
        const stepEl = opt.closest("gg-quiz-step");
        fireCustomGA4Event("gg_click_quiz_option", {
          step: stepEl ? stepEl.dataset.ggQuizStep : "",
          tag: opt.dataset.ggQuizTag || "",
          label: (opt.textContent || "").trim(),
        });
      }
      return;
    }

    if (e.target.closest("[data-gg-quiz-reset]")) {
      this.reset();
      return;
    }

    const nav = e.target.closest("gg-quiz-nav");
    if (!nav) return;
    const dir = nav.dataset.dir;
    const current = this.currentStepIndex();
    if (current < 0) return;
    if (dir === "next") {
      if (current + 1 < this.steps.length) {
        this.show(current + 1);
        if (typeof fireCustomGA4Event === "function") {
          fireCustomGA4Event("gg_click_quiz_next", { from_step: current + 1 });
        }
      }
    } else if (dir === "back") {
      if (current > 0) {
        this.show(current - 1);
        if (typeof fireCustomGA4Event === "function") {
          fireCustomGA4Event("gg_click_quiz_back", { from_step: current + 1 });
        }
      }
    } else if (dir === "submit") {
      this.showResult();
    }
  }
}

customElements.define("gg-quiz", GGQuiz);

class GGQuizStep extends HTMLElement {}
customElements.define("gg-quiz-step", GGQuizStep);

class GGQuizOption extends HTMLElement {}
customElements.define("gg-quiz-option", GGQuizOption);

class GGQuizNav extends HTMLElement {}
customElements.define("gg-quiz-nav", GGQuizNav);

class GGQuizResult extends HTMLElement {}
customElements.define("gg-quiz-result", GGQuizResult);

class GGQuizVariant extends HTMLElement {}
customElements.define("gg-quiz-variant", GGQuizVariant);

class GGTips extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-gg-tips-track]");
    this.prevBtn = this.querySelector("[data-gg-tips-prev]");
    this.nextBtn = this.querySelector("[data-gg-tips-next]");
    if (!this.track) return;

    this.onPrev = () => this.scrollByDir(-1);
    this.onNext = () => this.scrollByDir(1);
    this.onScroll = this.updateButtons.bind(this);
    this.updateButtons = this.updateButtons.bind(this);

    if (this.prevBtn) this.prevBtn.addEventListener("click", this.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    // The result panel starts `hidden`, so the track is 0×0 when this
    // element first connects. ResizeObserver (unlike a plain window
    // "resize" listener) also fires when it's un-hidden and gets real
    // dimensions, so the prev/next buttons stay in sync.
    this.ro = new ResizeObserver(this.updateButtons);
    this.ro.observe(this.track);

    this.updateButtons();
  }

  disconnectedCallback() {
    if (this.prevBtn) this.prevBtn.removeEventListener("click", this.onPrev);
    if (this.nextBtn) this.nextBtn.removeEventListener("click", this.onNext);
    if (this.track) this.track.removeEventListener("scroll", this.onScroll);
    this.ro?.disconnect();
  }

  slideWidth() {
    const slide = this.track.querySelector(".gg-tips__slide");
    const gap = parseFloat(getComputedStyle(this.track).columnGap || "0") || 0;
    return slide
      ? slide.getBoundingClientRect().width + gap
      : this.track.clientWidth;
  }

  scrollByDir(dir) {
    this.track.scrollBy({ left: dir * this.slideWidth(), behavior: "smooth" });
  }

  updateButtons() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    if (this.prevBtn) this.prevBtn.disabled = x <= 1;
    if (this.nextBtn) this.nextBtn.disabled = x >= max - 1;
    this.classList.toggle("gg-tips--single", max <= 1);
  }
}
customElements.define("gg-tips", GGTips);

class GGProductModal extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.ggProductModal || "";
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
    GGProductModal.ensureGlobalListeners();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
  }

  static ensureGlobalListeners() {
    if (this._globalsInit) return;
    this._globalsInit = true;

    document.addEventListener("click", (e) => {
      const opener = e.target.closest("[data-gg-open-modal]");
      if (!opener) return;
      const id = opener.dataset.ggOpenModal;
      const scopeEl = opener.closest("[data-gg-products-scope], .gg-products");
      const selector = `gg-product-modal[data-gg-product-modal="${id}"]`;
      const modal =
        (scopeEl && scopeEl.querySelector(selector)) ||
        document.querySelector(selector);
      if (modal) {
        e.preventDefault();
        modal.open();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      const open = document.querySelector("gg-product-modal.is-open");
      if (open) open.close();
    });
  }

  open() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
    this.hidden = false;
    const content = this.querySelector(".gg-drawer-content");
    if (content) content.scrollTop = 0;
    requestAnimationFrame(() => {
      this.classList.add("is-open");
    });
    document.documentElement.style.overflow = "hidden";
    if (typeof fireCustomGA4Event === "function") {
      const titleEl = this.querySelector(".gg-drawer-title");
      fireCustomGA4Event("gg_open_product_modal", {
        product_id: this.productId,
        product_title: titleEl ? (titleEl.textContent || "").trim() : "",
      });
    }
  }

  close(immediate) {
    this.classList.remove("is-open");
    if (immediate) {
      this.hidden = true;
      return;
    }
    if (!document.querySelector("gg-product-modal.is-open")) {
      document.documentElement.style.overflow = "";
    }
    setTimeout(() => {
      this.hidden = true;
    }, 300);
    if (typeof fireCustomGA4Event === "function") {
      const titleEl = this.querySelector(".gg-drawer-title");
      fireCustomGA4Event("gg_close_product_modal", {
        product_id: this.productId,
        product_title: titleEl ? (titleEl.textContent || "").trim() : "",
      });
    }
  }

  onClick(e) {
    if (e.target.closest("[data-gg-drawer-close]")) {
      this.close();
      return;
    }

    const thumb = e.target.closest("[data-gg-thumb]");
    if (thumb) {
      const main = this.querySelector("[data-gg-main-img] img");
      if (main) {
        main.src = thumb.dataset.full;
        main.srcset = "";
        main.alt = thumb.dataset.alt || main.alt;
      }
      this.querySelectorAll("[data-gg-thumb]").forEach((t) => {
        t.classList.remove("is-active");
      });
      thumb.classList.add("is-active");
      return;
    }

    const ingr = e.target.closest("[data-gg-ingr]");
    if (ingr) {
      const explainer = this.querySelector("[data-gg-ingr-explainer]");
      this.querySelectorAll("[data-gg-ingr]").forEach((p) => {
        p.classList.remove("is-active");
        p.setAttribute("aria-expanded", "false");
      });
      ingr.classList.add("is-active");
      ingr.setAttribute("aria-expanded", "true");
      if (explainer) {
        const name = ingr.dataset.ingrName || "";
        const content = ingr.dataset.ingrContent || "";
        explainer.innerHTML =
          (name ? "<strong>" + name + "</strong>" : "") + content;
        explainer.hidden = false;
      }
      if (typeof fireCustomGA4Event === "function") {
        fireCustomGA4Event("gg_click_modal_ingredient", {
          product_id: this.productId,
          ingredient: ingr.dataset.ingrName || "",
        });
      }
    }
  }
}

customElements.define("gg-product-modal", GGProductModal);
